import React from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import { Card } from "react-bootstrap";

// Risk → color mapping
const RISK_COLOR_MAP = {
  HIGH: "#EF4444",
  MEDIUM: "#F59E0B",
  LOW: "#10B981",
  UNKNOWN: "#9CA3AF",
};

const DEFAULT_COLORS = ["#EF4444", "#F59E0B", "#10B981", "#9CA3AF"];

// Label uses BACKEND percent
const renderLabel = ({ name, payload }) => {
  const pct = payload?.percent;
  if (pct == null || pct === 0) return null;
  return `${name} (${pct.toFixed(2)}%)`;
};

// Tooltip
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const row = payload[0].payload;
  const pct = row.percent != null ? row.percent.toFixed(2) : "0.00";

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        padding: 8,
        fontSize: 12,
        borderRadius: 4,
        color: "#111827",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{row.name}</div>
      <div>{`${row.value} cases`}</div>
      <div style={{ color: "#6b7280" }}>{`${pct}%`}</div>
    </div>
  );
};

const RiskSummaryAnalytics = ({ data, loading }) => {
  // -----------------------------
  // 1) LOADING
  // -----------------------------
  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div className="text-center py-4">
            <span style={{ fontSize: 12, color: "#6B7280" }}>
              Loading case risk summary…
            </span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // -----------------------------
  // 2) NO DATA
  // -----------------------------
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <p style={{ fontSize: 12, color: "#6B7280", marginBottom: 0 }}>
            No case risk data available for this period.
          </p>
        </Card.Body>
      </Card>
    );
  }

  // -----------------------------
  // 3) FILTER ZERO VALUES
  // -----------------------------
  const normalized = data.filter((d) => (d.value ?? 0) > 0);

  const total = normalized.reduce((sum, d) => sum + d.value, 0);

  // -----------------------------
  // 4) RENDER
  // -----------------------------
  return (
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <div style={{ marginBottom: 10 }}>
          <h6
            style={{
              fontWeight: 600,
              marginBottom: 2,
              fontSize: 14,
              color: "#111827",
            }}
          >
            Case Risk Summary
          </h6>
          <p style={{ fontSize: 12, color: "#6B7280", marginBottom: 0 }}>
            Distribution of cases classified as HIGH / MEDIUM / LOW. Total cases:{" "}
            <strong>{total}</strong>
          </p>
        </div>

        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={normalized}
                dataKey="value"
                nameKey="name"
                innerRadius="45%"
                outerRadius="80%"
                paddingAngle={2}
                labelLine={false}
                label={renderLabel}
              >
                {normalized.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      RISK_COLOR_MAP[entry.name] ||
                      DEFAULT_COLORS[index % DEFAULT_COLORS.length]
                    }
                  />
                ))}
              </Pie>

              <Tooltip content={<CustomTooltip />} />

              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                formatter={(value) => (
                  <span style={{ fontSize: 12, color: "#374151" }}>
                    {value}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Card.Body>
    </Card>
  );
};

export default RiskSummaryAnalytics;
